import React, { useState, useEffect, useCallback } from 'react';
import { Asset } from '@/api/types';
import { useAssets, useTelegram } from '@/hooks';
import { LoadingSpinner } from '@/components/common';
import { formatPrice, getCurrencySymbol } from '@/utils/formatting';

interface AssetSearchProps {
  onSelect: (asset: Asset) => void;
  selectedAsset?: Asset | null;
}

export const AssetSearch: React.FC<AssetSearchProps> = ({ onSelect, selectedAsset }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const { assets, isLoading, searchAssets, clearAssets } = useAssets();
  const { impactOccurred, selectionChanged } = useTelegram();

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm.length >= 1) {
        searchAssets({ search: searchTerm, perPage: 15 });
      } else if (searchTerm.length === 0 && isOpen) {
        // Show popular assets when search is empty
        searchAssets({ perPage: 10 });
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm, searchAssets, isOpen]);

  const handleFocus = useCallback(() => {
    setIsOpen(true);
    if (!searchTerm) {
      searchAssets({ perPage: 10 });
    }
  }, [searchAssets, searchTerm]);

  const handleSelect = useCallback(
    (asset: Asset) => {
      impactOccurred('light');
      selectionChanged();
      onSelect(asset);
      setSearchTerm('');
      setIsOpen(false);
      clearAssets();
    },
    [onSelect, impactOccurred, selectionChanged, clearAssets]
  );

  const handleClear = useCallback(() => {
    setSearchTerm('');
    setIsOpen(false);
    clearAssets();
  }, [clearAssets]);

  // Get USDT price from asset
  const getUSDTPrice = (asset: Asset): number | null => {
    const usdtPrice = asset.prices?.find((p) => p.currency?.symbol === 'USDT');
    return usdtPrice?.price || null;
  };

  return (
    <div style={{ position: 'relative', marginBottom: '16px' }}>
      <label
        style={{
          display: 'block',
          marginBottom: '8px',
          fontSize: '14px',
          fontWeight: 500,
          color: 'var(--tg-theme-text-color)',
        }}
      >
        Select Asset
      </label>

      {/* Selected asset display */}
      {selectedAsset && !isOpen ? (
        <div
          onClick={() => setIsOpen(true)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '12px 16px',
            backgroundColor: 'var(--tg-theme-secondary-bg-color)',
            borderRadius: '12px',
            cursor: 'pointer',
          }}
        >
          {selectedAsset.image && (
            <img
              src={selectedAsset.image}
              alt={selectedAsset.symbol}
              style={{ width: '32px', height: '32px', borderRadius: '50%' }}
            />
          )}
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, color: 'var(--tg-theme-text-color)' }}>
              {selectedAsset.symbol}
            </div>
            <div style={{ fontSize: '13px', color: 'var(--tg-theme-hint-color)' }}>
              {selectedAsset.name}
            </div>
          </div>
          <span style={{ color: 'var(--tg-theme-link-color)', fontSize: '14px' }}>Change</span>
        </div>
      ) : (
        <>
          {/* Search input */}
          <div style={{ position: 'relative' }}>
            <input
              type="text"
              placeholder="Search by symbol or name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onFocus={handleFocus}
              style={{
                width: '100%',
                padding: '14px 16px',
                paddingRight: isLoading ? '44px' : '16px',
                fontSize: '16px',
                border: '1px solid var(--tg-theme-hint-color)',
                borderRadius: '12px',
                backgroundColor: 'var(--tg-theme-secondary-bg-color)',
                color: 'var(--tg-theme-text-color)',
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />
            {isLoading && (
              <div
                style={{
                  position: 'absolute',
                  right: '14px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                }}
              >
                <LoadingSpinner size="small" />
              </div>
            )}
          </div>

          {/* Results dropdown */}
          {isOpen && (
            <div
              style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                maxHeight: '300px',
                overflowY: 'auto',
                backgroundColor: 'var(--tg-theme-bg-color)',
                border: '1px solid var(--tg-theme-hint-color)',
                borderRadius: '12px',
                marginTop: '4px',
                zIndex: 100,
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              }}
            >
              {selectedAsset && (
                <div
                  onClick={handleClear}
                  style={{
                    padding: '12px 16px',
                    borderBottom: '1px solid var(--tg-theme-secondary-bg-color)',
                    color: 'var(--tg-theme-link-color)',
                    cursor: 'pointer',
                    fontSize: '14px',
                  }}
                >
                  Cancel
                </div>
              )}

              {assets.length === 0 && !isLoading && searchTerm.length > 0 && (
                <div
                  style={{
                    padding: '24px 16px',
                    textAlign: 'center',
                    color: 'var(--tg-theme-hint-color)',
                  }}
                >
                  No assets found for "{searchTerm}"
                </div>
              )}

              {assets.map((asset) => {
                const price = getUSDTPrice(asset);

                return (
                  <div
                    key={asset.id}
                    onClick={() => handleSelect(asset)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '12px 16px',
                      cursor: 'pointer',
                      borderBottom: '1px solid var(--tg-theme-secondary-bg-color)',
                    }}
                  >
                    {asset.image && (
                      <img
                        src={asset.image}
                        alt={asset.symbol}
                        style={{ width: '36px', height: '36px', borderRadius: '50%' }}
                      />
                    )}
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, color: 'var(--tg-theme-text-color)' }}>
                        {asset.symbol}
                      </div>
                      <div style={{ fontSize: '13px', color: 'var(--tg-theme-hint-color)' }}>
                        {asset.name}
                      </div>
                    </div>
                    {price && (
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontWeight: 500, color: 'var(--tg-theme-text-color)' }}>
                          {getCurrencySymbol('USDT')}{formatPrice(price)}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
};
